// src/layouts/MainLayout/index.js
// ==========================================================
// メインレイアウト（サイドバー／ヘッダー／フッター）
// - RBAC（権限）によるメニュー表示制御
// - presence のハートビート開始（オンライン人数用）
// - 「オンライン中」タブは onlineList.view 権限を持つユーザーのみ表示
// - ログアウト時に presence を即時オフライン化してからサインアウト
// ==========================================================

import React, { useMemo, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  UserOutlined,
  SettingOutlined,
  SunOutlined,
  MoonOutlined,
  LogoutOutlined,
  HomeOutlined,
  BookOutlined,
  IdcardOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Layout, Menu, Switch, Typography, Avatar, Dropdown, Space } from "antd";

import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { usePresenceHeartbeat } from "../../hooks/usePresence";
import OnlineUsers from "../../components/OnlineUsers";

import { auth } from "../../firebase-config";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";

import "./styles.css";

const { Header, Content, Sider, Footer } = Layout;
const { Text, Title } = Typography;

/**
 * サイドバー基本メニュー
 * - permissionKey で権限（view）をチェック
 * - 「オンライン中」は base に含めず、後で onlineList.view に応じて動的に挿入
 */
const baseMenuItems = [
  { label: "ホームページ", key: "/", permissionKey: "dashboard", icon: <HomeOutlined /> },
  { label: "マイページ", key: "/my-page", permissionKey: "myPage", icon: <IdcardOutlined /> },
  { label: "テスト実施", key: "/exams", permissionKey: "takeExam", icon: <FileTextOutlined /> },
  { type: "divider" },
  { label: "簿記３級", key: "/level/level1", permissionKey: "level1", icon: <BookOutlined /> },
  { label: "簿記２級", key: "/level/level2", permissionKey: "level2", icon: <BookOutlined /> },
  { label: "日商簿記", key: "/level/level3", permissionKey: "level3", icon: <BookOutlined /> },
  { type: "divider" },
  // ★ 仕訳練習：route mới /journal-practice
  {
    label: "仕訳練習",
    key: "/journal-practice",
    permissionKey: "journalPractise",
    icon: <BookOutlined />,
  },
  { type: "divider" },
  { label: "テスト管理", key: "/tests", permissionKey: "tests", icon: <UnorderedListOutlined /> },
  { type: "divider" },
  { label: "設定", key: "/setting", permissionKey: "setting", icon: <SettingOutlined /> },
];

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, userRole, logout, canView } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // ----------------------------------------------------------
  // 🔐 匿名認証（保険）
  // ----------------------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => {});
    if (!auth.currentUser) {
      signInAnonymously(auth).catch((e) =>
        console.warn("[auth] anonymous sign-in failed", e)
      );
    }
    return () => unsub();
  }, []);

  // ----------------------------------------------------------
  // presence 用プロフィール
  // ----------------------------------------------------------
  const appUid =
    (currentUser &&
      (currentUser.uid ||
        currentUser.id ||
        currentUser.userId ||
        currentUser.regNumber ||
        currentUser.email)) ||
    null;

  const userForPresence = appUid
    ? {
        uid: String(appUid),
        fullName: currentUser?.fullName,
        nameRomaji: currentUser?.nameRomaji,
        displayName: currentUser?.fullName || currentUser?.nameRomaji,
        avatar: currentUser?.avatar,
        photoURL: currentUser?.photoURL,
        className: currentUser?.className,
        regNumber: currentUser?.regNumber,
      }
    : null;

  // ----------------------------------------------------------
  // 💓 presence ハートビート開始
  // ----------------------------------------------------------
  const { stop: stopPresence } = usePresenceHeartbeat(userForPresence, {
    intervalMs: 25000,
    staleMs: 120000,
    extra: {
      className: currentUser?.className ?? null,
      regNumber: currentUser?.regNumber ?? null,
      role: currentUser?.role ?? null,
    },
  });

  // ----------------------------------------------------------
  // 権限(view)でメニューをフィルタ
  // ----------------------------------------------------------
  const filteredMenuItems = useMemo(() => {
    if (!userRole || !userRole.permissions) return [];

    const permissions = userRole.permissions;

    const accessible = baseMenuItems.filter((item) => {
      if (item.type === "divider") return true;

      const k = item.permissionKey;
      if (k === "setting") {
        return (
          (permissions.students && permissions.students.view) ||
          (permissions.teachers && permissions.teachers.view) ||
          (permissions.roles && permissions.roles.view) ||
          (permissions.permissions && permissions.permissions.view)
        );
      }
      return permissions[k] && permissions[k].view;
    });

    return accessible.map(({ permissionKey, ...rest }) => rest);
  }, [userRole]);

  // ----------------------------------------------------------
  // 「オンライン中」タブを動的に挿入
  // ----------------------------------------------------------
  let menuItems = filteredMenuItems;
  if (canView && canView("onlineList")) {
    const onlineItem = {
      label: "オンライン中",
      key: "/online",
      icon: <UserOutlined />,
    };
    const idx = menuItems.findIndex((i) => i.key === "/my-page");
    menuItems = [...filteredMenuItems];
    menuItems.splice(idx >= 0 ? idx + 1 : 1, 0, onlineItem);
  }

  // メニュークリックで遷移
  const handleMenuClick = (e) => navigate(e.key);

  // ----------------------------------------------------------
  // ログアウト
  // ----------------------------------------------------------
  const handleLogout = async () => {
    try {
      await stopPresence();
    } catch (e) {
      /* best-effort */
    }
    await logout();
  };

  const dropdownItems = [
    {
      key: "info",
      disabled: true,
      label: (
        <div>
          <Text strong>
            {currentUser?.fullName || currentUser?.nameRomaji || ""}
          </Text>
          <br />
          <Text type="secondary">
            {currentUser?.className && currentUser?.regNumber
              ? `${currentUser.className}-${currentUser.regNumber}`
              : currentUser?.className || ""}
          </Text>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "logout",
      label: "ログアウト",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="main-layout">
      {/* ===== サイドバー ===== */}
      <Sider theme={theme} className="main-layout-sider">
        <div className="logo-vertical" />

        <Menu
          theme={theme}
          selectedKeys={[location.pathname]}
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
          className="sidebar-menu"
        />

        {/* テーマ切替（ダーク/ライト） */}
        <div className="theme-switch-container">
          <SunOutlined />
          <Switch
            checked={theme === "dark"}
            onChange={toggleTheme}
            style={{ margin: "0 8px" }}
          />
          <MoonOutlined />
        </div>
      </Sider>

      {/* ===== 右側：ヘッダー／本文／フッター ===== */}
      <Layout>
        <Header className="site-layout-header">
          <Title level={4} className="header-title">
            東亜-簿記練習システム
          </Title>

          <OnlineUsers />

          <Dropdown menu={{ items: dropdownItems }} placement="bottomRight">
            <div className="header-right">
              <Space>
                <Avatar src={currentUser?.avatar} icon={<UserOutlined />} />
                <Text strong>
                  {currentUser?.fullName || currentUser?.nameRomaji || ""}
                </Text>
              </Space>
            </div>
          </Dropdown>
        </Header>

        <Content className="site-layout-content">
          <Outlet />
        </Content>

        <Footer className="site-layout-footer">
          ©{new Date().getFullYear()} Toa Accounting System・All Rights Reserved.
        </Footer>
      </Layout>
    </Layout>
  );
}
